import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from './ui/dialog';
import { toast } from 'sonner';
import { Plus, Trash2, Edit2, Star, GripVertical, Check } from 'lucide-react';
import { Switch } from './ui/switch';
import { Badge } from './ui/badge';
import { useDialog } from '@/context/DialogContext';
import api from '@/lib/api';

const FIELD_TYPES = [
  { value: 'text', label: 'Text Input' },
  { value: 'textarea', label: 'Long Text' },
  { value: 'number', label: 'Number' },
  { value: 'rating', label: 'Rating (1-5)' },
  { value: 'dropdown', label: 'Dropdown' },
  { value: 'yes_no', label: 'Yes/No' }
];

export default function PerformanceReviewFormsTab() {
  const { confirm } = useDialog();
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingForm, setEditingForm] = useState(null);
  
  // Form state
  const [formName, setFormName] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formFields, setFormFields] = useState([]);
  const [isDefault, setIsDefault] = useState(false);

  useEffect(() => {
    fetchForms();
  }, []);

  const fetchForms = async () => {
    try {
      const res = await api.get('/performance-review-forms');
      setForms(res.data || []);
    } catch (err) {
      toast.error('Failed to load forms');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (form = null) => {
    if (form) {
      setEditingForm(form);
      setFormName(form.name);
      setFormDescription(form.description || '');
      setFormFields(form.fields || []);
      setIsDefault(form.is_default || false);
    } else {
      setEditingForm(null);
      setFormName('');
      setFormDescription('');
      setFormFields([]);
      setIsDefault(false);
    }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formName.trim()) {
      toast.error('Form name is required');
      return;
    }

    const payload = {
      name: formName,
      description: formDescription,
      fields: formFields.map((f, idx) => ({ ...f, order: idx })),
      is_default: isDefault
    };

    try {
      if (editingForm) {
        await api.patch(`/performance-review-forms/${editingForm.id}`, payload);
      } else {
        await api.post('/performance-review-forms', payload);
      }
      toast.success(editingForm ? 'Form updated' : 'Form created');
      setDialogOpen(false);
      fetchForms();
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Failed to save form';
      toast.error(typeof msg === 'string' ? msg : 'Failed to save form');
    }
  };

  const handleDelete = async (formId) => {
    const ok = await confirm({
      title: "Delete this form?",
      description: "Existing reviews already using this form will keep their data, but the form will no longer be available for new reviews.",
      confirmText: "Delete",
      variant: "destructive",
    });
    if (!ok) return;

    try {
      await api.delete(`/performance-review-forms/${formId}`);
      toast.success('Form deleted');
      fetchForms();
    } catch (err) {
      toast.error('Failed to delete form');
    }
  };

  const addField = () => {
    setFormFields([...formFields, {
      key: `field_${Date.now()}`,
      label: '',
      type: 'text',
      required: false,
      options: [],
      help_text: ''
    }]);
  };

  const updateField = (index, updates) => {
    const updated = [...formFields];
    updated[index] = { ...updated[index], ...updates };
    setFormFields(updated);
  };

  const removeField = (index) => {
    setFormFields(formFields.filter((_, i) => i !== index));
  };

  const moveField = (index, direction) => {
    if ((direction === 'up' && index === 0) || (direction === 'down' && index === formFields.length - 1)) return;
    const newFields = [...formFields];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    [newFields[index], newFields[targetIndex]] = [newFields[targetIndex], newFields[index]];
    setFormFields(newFields);
  };

  if (loading) return <div className="text-center py-8">Loading...</div>;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold">Performance Review Forms</h3>
          <p className="text-sm text-muted-foreground">Customize review forms with flexible field types</p>
        </div>
        <Button onClick={() => handleOpenDialog()}>
          <Plus className="h-4 w-4 mr-2" />
          New Form
        </Button>
      </div>

      <div className="grid gap-4">
        {forms.map(form => (
          <Card key={form.id}>
            <CardHeader>
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <CardTitle className="text-base">{form.name}</CardTitle>
                    {form.is_default && <Badge variant="default">Default</Badge>}
                  </div>
                  {form.description && (
                    <CardDescription className="mt-1">{form.description}</CardDescription>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => handleOpenDialog(form)}>
                    <Edit2 className="h-4 w-4" />
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => handleDelete(form.id)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-sm text-muted-foreground">
                {form.fields?.length || 0} fields configured
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Form Editor Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingForm ? 'Edit' : 'Create'} Review Form</DialogTitle>
            <DialogDescription>
              Define custom fields for performance reviews
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label>Form Name</Label>
              <Input value={formName} onChange={(e) => setFormName(e.target.value)} placeholder="Annual Review 2026" />
            </div>

            <div>
              <Label>Description (Optional)</Label>
              <Textarea value={formDescription} onChange={(e) => setFormDescription(e.target.value)} placeholder="Describe the purpose of this form" rows={2} />
            </div>

            <div className="flex items-center gap-2">
              <Switch checked={isDefault} onCheckedChange={setIsDefault} />
              <Label>Set as default form</Label>
            </div>

            <div className="border-t pt-4">
              <div className="flex justify-between items-center mb-3">
                <Label className="text-base">Form Fields</Label>
                <Button size="sm" onClick={addField}>
                  <Plus className="h-4 w-4 mr-1" />
                  Add Field
                </Button>
              </div>

              <div className="space-y-3">
                {formFields.map((field, idx) => (
                  <Card key={idx}>
                    <CardContent className="pt-4 space-y-3">
                      <div className="flex items-start gap-2">
                        <div className="flex flex-col gap-1 mt-2">
                          <Button size="sm" variant="ghost" onClick={() => moveField(idx, 'up')} disabled={idx === 0}>
                            ↑
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => moveField(idx, 'down')} disabled={idx === formFields.length - 1}>
                            ↓
                          </Button>
                        </div>
                        <div className="flex-1 space-y-3">
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <Label>Field Label</Label>
                              <Input value={field.label} onChange={(e) => updateField(idx, { label: e.target.value })} placeholder="e.g., Goals Achievement" />
                            </div>
                            <div>
                              <Label>Field Type</Label>
                              <Select value={field.type} onValueChange={(val) => updateField(idx, { type: val })}>
                                <SelectTrigger>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {FIELD_TYPES.map(t => (
                                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          </div>

                          {field.type === 'dropdown' && (
                            <div>
                              <Label>Options (comma-separated)</Label>
                              <Input 
                                value={field.options.join(', ')} 
                                onChange={(e) => updateField(idx, { options: e.target.value.split(',').map(o => o.trim()) })} 
                                placeholder="Excellent, Good, Average, Poor" 
                              />
                            </div>
                          )}

                          <div>
                            <Label>Help Text (Optional)</Label>
                            <Input value={field.help_text || ''} onChange={(e) => updateField(idx, { help_text: e.target.value })} placeholder="Guidance for this field" />
                          </div>

                          <div className="flex items-center gap-2">
                            <Switch checked={field.required} onCheckedChange={(val) => updateField(idx, { required: val })} />
                            <Label>Required field</Label>
                          </div>
                        </div>
                        <Button size="sm" variant="ghost" onClick={() => removeField(idx)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave}>Save Form</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
